import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { useSearchParams } from "react-router-dom";
import {
  Box, Typography, Button, Stack, Chip, LinearProgress,
} from "@mui/material";
import {
  WhatsApp, Share, ArrowForward, ContentCopy, Check,
  HowToVote, CheckCircle, BarChart,
} from "@mui/icons-material";

const DOMAIN   = "https://drpawleb.com";
const DISPLAY  = '"Cormorant Garamond", Georgia, serif';
const BLUE     = "#1565C0";
const BABY     = "#42A5F5";

const OPTION_STYLES = [
  { bar: "linear-gradient(90deg,#1565C0,#42A5F5)", border: "rgba(66,165,245,.4)",  bg: "rgba(66,165,245,.08)",  text: "#1565C0" },
  { bar: "linear-gradient(90deg,#059669,#34D399)", border: "rgba(52,211,153,.4)",  bg: "rgba(52,211,153,.08)",  text: "#059669" },
  { bar: "linear-gradient(90deg,#D97706,#FBBF24)", border: "rgba(251,191,36,.4)",  bg: "rgba(251,191,36,.08)",  text: "#D97706" },
  { bar: "linear-gradient(90deg,#7C3AED,#A78BFA)", border: "rgba(167,139,250,.4)", bg: "rgba(167,139,250,.08)", text: "#7C3AED" },
];

const DrPawLogo: React.FC = () => (
  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
    <Box sx={{
      width: 42, height: 42, borderRadius: "12px",
      background: "linear-gradient(135deg,#1565C0,#42A5F5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 16px rgba(21,101,192,.35)",
    }}>
      <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: "1.05rem" }}>D</Typography>
    </Box>
    <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: "1.65rem", letterSpacing: "-.01em", fontFamily: DISPLAY }}>Dr.Paw</Typography>
  </Stack>
);

// ─────────────────────────────────────────────────────────────────────────────
const PublicPoll: React.FC = () => {
  const [searchParams] = useSearchParams();
  const question = decodeURIComponent(searchParams.get("q") || "");
  const link     = searchParams.get("link") || `${DOMAIN}/#pricing`;

  const options = useMemo(() => {
    const result: { text: string; preset: number }[] = [];
    (["a","b","c","d"] as const).forEach((k) => {
      const text = searchParams.get(k);
      const pct  = parseInt(searchParams.get(`v${k}`) ?? "0", 10);
      if (text) result.push({ text: decodeURIComponent(text), preset: isNaN(pct) ? 0 : pct });
    });
    return result;
  }, [searchParams]);

  const totalVotes = useMemo(() => {
    const h = new Date().getHours();
    const m = new Date().getMinutes();
    const base = h >= 8 && h <= 11 ? 640 : h >= 18 && h <= 22 ? 890 : h >= 12 && h <= 14 ? 720 : 380;
    return base + (m % 60) * 4;
  }, []);

  const [voted,  setVoted]  = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [reveal, setReveal] = useState(false);

  const handleVote = (idx: number) => {
    if (voted !== null) return;
    setVoted(idx);
    setTimeout(() => setReveal(true), 150);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShare = async () => {
    const chosenText = voted !== null ? options[voted]?.text : "";
    const shareData = {
      title: "Dr.Paw Community Poll",
      text: `I just voted on this vet poll by Dr.Paw! 🐾\n\n"${question}"\nMy answer: "${chosenText}"`,
      url: window.location.href,
    };
    if (typeof navigator.share === "function") {
      try { await navigator.share(shareData); return; } catch {}
    }
    handleCopy();
  };

  const percentages = useMemo(() => {
    if (voted === null) return options.map(() => 0);
    const hasPresets = options.some((o) => o.preset > 0);
    if (hasPresets) {
      const total = options.reduce((s, o) => s + o.preset, 0) || 100;
      return options.map((o) => Math.round((o.preset / total) * 100));
    }
    const fallbacks = [[58, 42], [47, 31, 22], [40, 30, 20, 10]];
    const base = [...(fallbacks[options.length - 2] ?? fallbacks[0])];
    const topVal = base[0];
    const votedVal = base[voted] ?? base[0];
    base[0]     = votedVal;
    base[voted] = topVal;
    return base;
  }, [voted, options]);

  if (!question || options.length < 2) {
    return (
      <Box sx={{ minHeight: "100vh", background: "#F8F3EE", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: "#90A4AE" }}>Invalid or missing poll link.</Typography>
      </Box>
    );
  }

  const chosenPct = voted !== null ? (percentages[voted] ?? 0) : 0;
  const waMsg = encodeURIComponent(
    `I just voted on this vet poll by Dr.Paw! 🐾\n\n"${question}"\n\nMy answer: "${options[voted ?? 0]?.text}" — and ${chosenPct}% of vets agreed! 🎯\n\nVote here: ${window.location.href}`
  );
  const waUrl = `https://wa.me/?text=${waMsg}`;

  const metaTitle = question ? `Poll: ${question.slice(0, 60)} — Dr.Paw` : "Vet Community Poll — Dr.Paw";
  const metaDesc  = question
    ? `"${question}" — Vote and see what the vet community thinks. Dr.Paw veterinary education.`
    : "Share your opinion with the Dr.Paw veterinary community. Vote now and see the results!";

  return (
    <>
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content="https://drpawleb.com/og-image.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
    </Helmet>
    <HreflangTags />
    <Box sx={{
      minHeight: "100vh",
      background: "linear-gradient(160deg,#FEF9F5 0%,#F8F3EE 55%,#F2EDE6 100%)",
      display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
    }}>
      <Box sx={{ position: "absolute", top: -100, right: -100, width: 450, height: 450, borderRadius: "50%", background: "radial-gradient(circle,rgba(21,101,192,.07) 0%,transparent 70%)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle,rgba(66,165,245,.06) 0%,transparent 70%)", pointerEvents: "none" }} />

      {/* Header */}
      <Box sx={{ pt: 4, pb: 2.5, px: 3, textAlign: "center" }}>
        <DrPawLogo />
        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: BABY, fontWeight: 600, fontSize: ".7rem", letterSpacing: ".15em", textTransform: "uppercase" }}>
          Community Poll
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ width: "100%", maxWidth: 580 }}>

          {/* Poll card */}
          <Box sx={{
            background: "#fff",
            border: "1px solid rgba(21,101,192,.15)",
            borderRadius: "28px",
            boxShadow: "0 4px 24px rgba(21,101,192,.07)",
            p: { xs: 3, md: 4 }, mb: 2.5,
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
              <Chip
                icon={<HowToVote sx={{ fontSize: ".9rem !important", color: `${BLUE} !important` }} />}
                label="Vet Poll"
                sx={{ bgcolor: "rgba(21,101,192,.08)", color: BLUE, fontWeight: 700, fontSize: ".75rem", border: `1px solid rgba(21,101,192,.2)` }}
              />
              <Chip
                icon={<BarChart sx={{ fontSize: ".85rem !important", color: "#059669 !important" }} />}
                label={`${(totalVotes + (voted !== null ? 1 : 0)).toLocaleString()} votes`}
                size="small"
                sx={{ bgcolor: "rgba(34,197,94,.08)", color: "#059669", fontWeight: 600, fontSize: ".72rem" }}
              />
            </Stack>

            <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: { xs: "1.4rem", md: "1.75rem" }, lineHeight: 1.3, letterSpacing: "-.01em", mb: 3, fontFamily: DISPLAY }}>
              {question}
            </Typography>

            <Stack spacing={1.5}>
              {options.map((opt, idx) => {
                const s   = OPTION_STYLES[idx % OPTION_STYLES.length];
                const pct = percentages[idx];
                const isVoted = voted === idx;

                if (!reveal) return (
                  <Box
                    key={idx}
                    onClick={() => handleVote(idx)}
                    sx={{
                      p: { xs: 1.8, md: 2 }, borderRadius: "14px", cursor: voted !== null ? "default" : "pointer",
                      background: voted === null ? "#fff" : isVoted ? s.bg : "#F8F9FB",
                      border: `1px solid ${voted === null ? "rgba(21,101,192,.12)" : isVoted ? s.border : "rgba(21,101,192,.06)"}`,
                      display: "flex", alignItems: "center", gap: 1.5,
                      transition: "all .2s ease",
                      "&:hover": voted !== null ? {} : { background: s.bg, borderColor: s.border, transform: "translateX(5px)" },
                    }}
                  >
                    <Box sx={{ width: 34, height: 34, borderRadius: "9px", bgcolor: s.bg, border: `1px solid ${s.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Typography sx={{ color: s.text, fontWeight: 800, fontSize: ".8rem" }}>{String.fromCharCode(65 + idx)}</Typography>
                    </Box>
                    <Typography sx={{ color: "#37474F", fontWeight: 500, fontSize: ".93rem", flex: 1, lineHeight: 1.35 }}>{opt.text}</Typography>
                  </Box>
                );

                return (
                  <Box key={idx} sx={{ p: { xs: 1.8, md: 2 }, borderRadius: "14px", background: isVoted ? s.bg : "#F8F9FB", border: `1px solid ${isVoted ? s.border : "rgba(21,101,192,.07)"}` }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.8}>
                        {isVoted && <CheckCircle sx={{ color: s.text, fontSize: "1rem" }} />}
                        <Typography sx={{ color: isVoted ? s.text : "#546E7A", fontWeight: isVoted ? 700 : 500, fontSize: ".88rem", lineHeight: 1.35 }}>
                          {opt.text}
                        </Typography>
                      </Stack>
                      <Typography sx={{ color: isVoted ? s.text : "#90A4AE", fontWeight: 700, fontSize: ".88rem", ml: 1.5, flexShrink: 0 }}>{pct}%</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate" value={pct}
                      sx={{ height: 7, borderRadius: 99, bgcolor: "rgba(21,101,192,.08)", "& .MuiLinearProgress-bar": { background: s.bar, borderRadius: 99, transition: "transform 0.8s cubic-bezier(0.4,0,0.2,1)" } }}
                    />
                  </Box>
                );
              })}
            </Stack>

            {reveal && (
              <Box sx={{ mt: 2.5, p: 2, borderRadius: "12px", background: "rgba(21,101,192,.06)", border: "1px solid rgba(21,101,192,.18)", textAlign: "center" }}>
                <Typography sx={{ color: BLUE, fontWeight: 700, fontSize: ".9rem" }}>
                  {chosenPct >= 50
                    ? `${chosenPct}% of vets agree with you! 🎯 The majority's choice.`
                    : chosenPct >= 30
                    ? `${chosenPct}% of vets voted the same. You're in the minority! 🤔`
                    : `Bold move — only ${chosenPct}% chose this. Stand by your conviction! 💪`}
                </Typography>
              </Box>
            )}
          </Box>

          {reveal && (
            <>
              <Box sx={{ background: "rgba(66,165,245,.06)", border: "1px solid rgba(66,165,245,.2)", borderRadius: "12px", px: 2.5, py: 1.2, mb: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8 }}>
                <Typography sx={{ fontSize: ".8rem" }}>📱</Typography>
                <Typography sx={{ color: "#546E7A", fontSize: ".8rem" }}>
                  Screenshot your result and post with <strong style={{ color: BLUE }}>#DrPawPoll</strong>!
                </Typography>
              </Box>

              <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                <Button
                  variant="contained" startIcon={<Share />} onClick={handleShare} fullWidth
                  sx={{ py: 1.4, borderRadius: "50px", textTransform: "none", fontWeight: 700, fontSize: ".9rem", background: "linear-gradient(135deg,#7C3AED,#A855F7)", boxShadow: "0 4px 14px rgba(124,58,237,.25)", "&:hover": { background: "linear-gradient(135deg,#6D28D9,#7C3AED)", transform: "translateY(-1px)" }, transition: "all .2s ease" }}
                >
                  Share My Vote to Story
                </Button>
                <Button
                  variant="outlined" startIcon={<WhatsApp />} href={waUrl} target="_blank" fullWidth
                  sx={{ py: 1.3, borderRadius: "50px", textTransform: "none", fontWeight: 700, borderColor: "#25D366", color: "#25D366", "&:hover": { bgcolor: "rgba(37,211,102,.08)", borderColor: "#25D366" } }}
                >
                  Share Result on WhatsApp
                </Button>
              </Stack>

              {/* CTA */}
              <Box sx={{ background: "#fff", border: "1px solid rgba(21,101,192,.15)", borderRadius: "20px", boxShadow: "0 4px 16px rgba(21,101,192,.06)", p: { xs: 2.5, md: 3 }, mb: 2 }}>
                <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: ".95rem", mb: 1 }}>
                  Want to master topics like this?
                </Typography>
                <Typography sx={{ color: "#546E7A", fontSize: ".85rem", lineHeight: 1.75, mb: 2 }}>
                  Expert-led Dr.Paw courses go deep — real cases, clinical guidance, practical skills.
                </Typography>
                <Button
                  variant="contained" href={link} endIcon={<ArrowForward />} fullWidth
                  sx={{ py: 1.4, borderRadius: "50px", textTransform: "none", fontWeight: 800, background: "linear-gradient(135deg,#1565C0,#42A5F5)", "&:hover": { background: "linear-gradient(135deg,#0D47A1,#1565C0)", transform: "translateY(-2px)" }, transition: "all .2s ease" }}
                >
                  Explore Dr.Paw Courses
                </Button>
              </Box>
            </>
          )}

          <Button
            variant="outlined" startIcon={copied ? <Check /> : <ContentCopy />} onClick={handleCopy} fullWidth
            sx={{ py: 1.2, borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: copied ? "rgba(34,197,94,.4)" : "rgba(21,101,192,.25)", color: copied ? "#22C55E" : "#78909C", bgcolor: "#fff", "&:hover": { borderColor: copied ? "rgba(34,197,94,.5)" : BLUE, color: copied ? "#22C55E" : BLUE }, transition: "all .2s ease" }}
          >
            {copied ? "Link Copied!" : "Copy Poll Link"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ py: 3, textAlign: "center", borderTop: "1px solid rgba(21,101,192,.1)" }}>
        <Typography variant="caption" sx={{ color: "#B0BEC5", fontSize: ".72rem" }}>
          © Dr.Paw — Empowering Veterinary Professionals Worldwide
        </Typography>
      </Box>
    </Box>
    </>
  );
};

export default PublicPoll;
